import { ChartDataset } from 'chart.js'
import { CHART_COLORS, getBarChartDefaults } from 'components/charts/constants'
import { createChartDataArray } from 'utils/chartjs'
import { formatAmount } from 'utils/format'
import { ChartDatasetsParams, ChartOptionsParams } from './types'
import { externalTooltipHandler } from 'components/charts/chart-tooltip/tooltip.ts'
import { TOOLTIP_CONFIG } from 'components/charts/chart-tooltip/config.ts'

/**
 * Get chart options
 * @param {ChartOptionsParams} params
 */
export const getSalesDashboardChartOptions = ({ labels, yAxisLabel, handleHoverDataPoint }: ChartOptionsParams) => {
  const defaults = getBarChartDefaults({ isMobile: false })
  const {
    scales: { y, x }
  } = defaults

  return {
    ...defaults,
    maintainAspectRatio: false,
    skipNull: true,
    // Keeps the hovered bar highlight in sync with the tooltip, which triggers on the nearest bar of the hovered column
    interaction: {
      intersect: false,
      mode: 'index'
    },
    scales: {
      y: {
        ...y,
        stacked: true,
        position: 'left',
        ticks: {
          ...y.ticks,
          callback: (value: number) => formatAmount(value, { fractionDigits: 0 }).toString()
        }
      },
      x: {
        ...x,
        stacked: true,
        ticks: {
          ...x.ticks,
          autoSkip: false,
          minRotation: 90,
          maxRotation: 90
        }
      }
    },
    plugins: {
      yScaleTitle: {
        labels: {
          y: yAxisLabel
        }
      },
      tooltip: {
        ...TOOLTIP_CONFIG,
        external: (context: any) => {
          const { tooltip } = context
          const currentDataPoint = tooltip?.dataPoints?.[0]?.dataIndex
          const hideTooltip = tooltip.opacity === 0
          handleHoverDataPoint(hideTooltip ? null : currentDataPoint)
          return externalTooltipHandler({ context, totalColumns: labels.length })
        }
      }
    }
  }
}

/**
 * Get chart datasets, one stack per given series
 * @param {ChartDatasetsParams} params
 */
export const getSalesDashboardChartDatasets = ({ labels, data, series }: ChartDatasetsParams): ChartDataset<'bar', (number | null)[]>[] => {
  const borderRadius = 2
  const maxBarThickness = 94

  return series.map(({ key, label, color }) => ({
    type: 'bar',
    label,
    backgroundColor: CHART_COLORS[color],
    data: createChartDataArray(
      labels?.length,
      data.map((entry) => entry.values[key] || 0)
    ),
    borderSkipped: 'start',
    maxBarThickness,
    borderRadius
  }))
}
